import { VegaButton, VegaButtonCircle, VegaFlex, VegaFont, VegaInputSelect } from '@globalpayments/vega-react';
import {
  KirbyFilterGroupNode,
  KirbyFilterNode,
  KirbyFilterPropertyDefinition,
  KirbyFilterValidationIssue,
} from '../../../types/kirbyFiltering';
import styles from '../index.module.css';
import FilterClauseRow from './FilterClauseRow';

interface FilterGroupProps {
  definitions: KirbyFilterPropertyDefinition[];
  depth: number;
  group: KirbyFilterGroupNode;
  groupPath: number[];
  issues: KirbyFilterValidationIssue[];
  onAddClause: (group: KirbyFilterGroupNode) => void;
  onAddGroup: (group: KirbyFilterGroupNode) => void;
  onNodeChange: (nodeId: string, update: (node: KirbyFilterNode) => KirbyFilterNode) => void;
  onRemoveNode: (nodeId: string) => void;
}

const conjunctionOptions = [
  { id: 'And', displayName: 'And' },
  { id: 'Or', displayName: 'Or' },
];

/** Renders one recursive group of Kirby clauses and child groups. */
const FilterGroup = ({
  definitions,
  depth,
  group,
  groupPath,
  issues,
  onAddClause,
  onAddGroup,
  onNodeChange,
  onRemoveNode,
}: FilterGroupProps) => {
  const isRootGroup = groupPath.length === 0;
  const groupIssue = issues.find((issue) => issue.nodeId === group.id)?.message;
  const showConjunction = !isRootGroup || group.children.length > 1;

  return (
    <VegaFlex
      className={depth > 0 ? styles.nestedGroup : styles.rootGroup}
      data-testid={group.id}
      direction="col"
      gap="size-12">
      <VegaFlex
        alignItems="start"
        flexWrap="wrap"
        gap="size-12"
        justifyContent={showConjunction ? 'space-between' : 'end'}>
        {showConjunction && (
          <VegaInputSelect
            data-testid={`${group.id}-conjunction`}
            label="Match"
            onVegaChange={(event) => {
              const conjunction = event.detail;
              if (conjunction === 'And' || conjunction === 'Or') {
                onNodeChange(group.id, (currentGroup) =>
                  currentGroup.kind === 'group' ? { ...currentGroup, conjunction } : currentGroup,
                );
              }
            }}
            selectType="single"
            source={conjunctionOptions}
            value={group.conjunction}
          />
        )}
        {depth > 0 && (
          <VegaButtonCircle
            icon="fa-solid fa-trash"
            label="Remove group"
            onVegaClick={() => onRemoveNode(group.id)}
            showTooltip
            size="small"
            variant="icon-only"
          />
        )}
      </VegaFlex>
      {groupIssue && (
        <VegaFont as="p" color="text-error" variant="font-p2-short">
          {groupIssue}
        </VegaFont>
      )}
      {group.children.map((node, index) => {
        if (node.kind === 'clause') {
          const clausePosition = group.children.slice(0, index + 1).filter((child) => child.kind === 'clause').length;
          return (
            <FilterClauseRow
              clause={node}
              definitions={definitions}
              index={clausePosition - 1}
              issues={issues.filter((issue) => issue.nodeId === node.id)}
              key={node.id}
              onChange={(update) =>
                onNodeChange(node.id, (currentNode) =>
                  currentNode.kind === 'clause' ? update(currentNode) : currentNode,
                )
              }
              onRemove={() => onRemoveNode(node.id)}
              removeDisabled={group.children.length === 1}
            />
          );
        }

        const groupPosition = group.children.slice(0, index + 1).filter((child) => child.kind === 'group').length;
        return (
          <FilterGroup
            definitions={definitions}
            depth={depth + 1}
            group={node}
            groupPath={[...groupPath, groupPosition]}
            issues={issues}
            key={node.id}
            onAddClause={onAddClause}
            onAddGroup={onAddGroup}
            onNodeChange={onNodeChange}
            onRemoveNode={onRemoveNode}
          />
        );
      })}
      <VegaFlex flexWrap="wrap" gap="size-8">
        <VegaButton
          icon="fa-solid fa-plus"
          iconAlign="left"
          label="Add Clause"
          onVegaClick={() => onAddClause(group)}
          size="small"
          variant="secondary"
        />
        <VegaButton
          icon="fa-solid fa-layer-group"
          iconAlign="left"
          label="Add Group"
          onVegaClick={() => onAddGroup(group)}
          size="small"
          variant="secondary"
        />
      </VegaFlex>
    </VegaFlex>
  );
};

export default FilterGroup;
